import { extract_error_message } from '@/utils/api_response_helpers';
import {
  delete_main_section as delete_main_section_request,
  soft_delete_form_section,
} from '@/services/main_section_service';

function getSubSectionId(ctx, section = {}) {
  return Number(
    section.section_id ??
      section.backend_id ??
      section.id ??
      ctx.current_edit_section?.section_id
  );
}

function getSubSectionMainSectionId(section = {}) {
  return Number(section.main_section_id ?? null);
}

function getSubSectionName(ctx, section = {}) {
  return String(
    section.name ??
      section.section_name ??
      ctx.current_edit_section?.name ??
      'this sub section'
  ).trim();
}

function buildDeleteSubSectionContext(ctx, section = {}) {
  const subsection = {
    section_id: getSubSectionId(ctx, section),
    form_id: Number(ctx.topBar.formId),
    main_section_id: getSubSectionMainSectionId(section),
    section_name: getSubSectionName(ctx, section),
  };
  return subsection;
}

async function executeDeleteSubSection(ctx, delete_context) {
  ctx.deleting_sub_section = true;
  ctx.set_sub_section_deleting(delete_context.section_id, true);

  const loader = await ctx.show_loader('Deleting sub section...');

  try {
    await performDeleteSubSection(ctx, delete_context);
  } finally {
    await ctx.close_loader(loader);
    ctx.deleting_sub_section = false;
    ctx.set_sub_section_deleting(delete_context.section_id, false);
  }
}

async function performDeleteSubSection(ctx, delete_context) {
  try {
    const parsed_response = await soft_delete_form_section({
      section_id: String(delete_context.section_id),
      form_id: String(delete_context.form_id),
    });

    const success =
      String(parsed_response?.status || '').toLowerCase() === 'success';

    if (!success) {
      throw new Error(
        parsed_response?.message || 'Failed to delete sub section.'
      );
    }

    ctx.close_modal('edit_sub_section_modal');
    ctx.close_edit_section_modal();

    await ctx.load_form_sections();

    if (delete_context.main_section_id) {
      ctx.expand_main_section_by_id(delete_context.main_section_id);
    }

    ctx.show_success(
      parsed_response.message || 'Section successfully deleted'
    );
  } catch (error) {
    ctx.show_error(
      extract_error_message(error, 'Failed to delete sub section.')
    );
  }
}

export function deleteSubSection(ctx, section = {}) {
  const delete_context = buildDeleteSubSectionContext(ctx, section);
  if (!delete_context.section_id || !delete_context.form_id) {
    ctx.show_error('Please select a valid sub section first.');
    return;
  }

  ctx.show_confirm('Delete Sub Section', `Are you sure you want to delete ${delete_context.section_name}?`, async () => {
    await executeDeleteSubSection(ctx, delete_context);
  });
}

function getMainSectionId(ctx, main_section = {}) {
  return Number(
    main_section.main_section_id ??
      main_section.id ??
      ctx.current_edit_main_section.main_section_id
  );
}

function getMainSectionName(ctx, main_section = {}) {
  return String(
    main_section.main_section_name ??
      main_section.name ??
      ctx.current_edit_main_section.main_section_name ??
      'this main section'
  ).trim();
}

function buildDeleteMainSectionContext(ctx, main_section = {}) {
  const delete_main_section = {
    main_section_id: getMainSectionId(ctx, main_section),
    form_id: Number(ctx.topBar.formId),
    main_section_name: getMainSectionName(ctx, main_section),
  };
  return delete_main_section;
}

async function startDeleteMainSection(ctx, delete_context) {
  ctx.deleting_main_section = true;
  ctx.set_main_section_deleting(delete_context.main_section_id, true);

  return ctx.show_loader('Deleting main section...');
}

async function finishDeleteMainSection(ctx, delete_context, loader) {
  await ctx.close_loader(loader);
  ctx.deleting_main_section = false;
  ctx.set_main_section_deleting(delete_context.main_section_id, false);
}

async function executeDeleteMainSection(ctx, delete_context) {
  const loader = await startDeleteMainSection(ctx, delete_context);

  try {
    await performDeleteMainSection(ctx, delete_context);
  } finally {
    await finishDeleteMainSection(ctx, delete_context, loader);
  }
}

async function performDeleteMainSection(ctx, delete_context) {
  try {
    const parsed_response = await delete_main_section_request({
      main_section_id: delete_context.main_section_id,
      form_id: delete_context.form_id,
    });

    const success =
      String(parsed_response?.status || '').toLowerCase() === 'success';

    if (!success) {
      ctx.show_error(
        parsed_response?.message || 'Failed to delete main section.'
      );
      return;
    }

    ctx.close_modal('edit_main_section_modal');
    ctx.close_edit_main_section_modal();

    await ctx.load_main_sections();

    ctx.show_success(
      parsed_response?.message || 'Main section deleted successfully.'
    );
  } catch (error) {
    ctx.show_error(
      extract_error_message(error, 'Failed to delete main section.')
    );
  }
}

export function deleteMainSection(ctx, main_section = {}) {
  const delete_context = buildDeleteMainSectionContext(ctx, main_section);
  if (!delete_context.main_section_id || !delete_context.form_id) {
    ctx.show_error('Please select a valid main section first.');
    return;
  }

  ctx.show_confirm('Delete Main Section', `Are you sure you want to delete ${delete_context.main_section_name}?`, async () => {
    await executeDeleteMainSection(ctx, delete_context);
  });
}
